import {
  assignDoctorPatientMutation,
  unassignDoctorPatientMutation,
} from '@/api/doctor-patients/doctorPatientsMutations';
import { getDoctorPatientsQuery } from '@/api/doctor-patients/doctorPatientsQueries';
import { queryKeys } from '@/api/queryKeys';
import type {
  UserWithClinicData,
  UsersFilters,
} from '@/api/users/usersQueries';
import { getAllUsersQuery, getUsersQuery } from '@/api/users/usersQueries';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { AssigningModificationsFilters } from './AssigningModificationsFilters';
import { ModifyRelationsDialog } from './ModifyRelationsDialog';

const INITIAL_ASSIGN_FILTERS: UsersFilters = { role: 'Doctor' };

export default function AssignModifications({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<UsersFilters>(INITIAL_ASSIGN_FILTERS);
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  const handleAccordionOpen = useCallback(() => {
    setFilters(INITIAL_ASSIGN_FILTERS);
    setPage(1);
    setFiltersResetKey((k) => k + 1);
  }, []);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithClinicData | null>(
    null,
  );

  const { data: response, isLoading } = useQuery(getUsersQuery(page, filters));

  const oppositeRole = filters.role === 'Doctor' ? 'Customer' : 'Doctor';
  const { data: oppositeUsers = [] } = useQuery(
    getAllUsersQuery({ role: oppositeRole }),
  );

  const users = response?.data || [];
  const meta = response?.meta;

  const handleModify = (user: UserWithClinicData) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const getClinicDisplay = (user: UserWithClinicData) => {
    if (user.clinics?.length) {
      return user.clinics
        .map((clinic) => clinic.clinic || clinic.name)
        .filter(Boolean)
        .join(', ');
    }

    return user.clinic?.name ?? '';
  };

  const { data: relationsResponse } = useQuery({
    ...getDoctorPatientsQuery({
      doctor_id: filters.role === 'Doctor' ? selectedUser?.id : undefined,
      user_id: filters.role === 'Customer' ? selectedUser?.id : undefined,
      per_page: 100,
    }),
    enabled: !!selectedUser,
  });

  const assignedIds =
    relationsResponse?.data.map((r) =>
      filters.role === 'Doctor' ? r.patient_id || r.user_id : r.doctor_id,
    ) || [];

  const { mutate: assign } = useMutation(assignDoctorPatientMutation());
  const { mutate: unassign } = useMutation(unassignDoctorPatientMutation());

  const handleAssign = (targetUserId: number) => {
    if (!selectedUser) return;
    const payload = {
      doctor_id: filters.role === 'Doctor' ? selectedUser.id : targetUserId,
      user_id: filters.role === 'Customer' ? selectedUser.id : targetUserId,
    };
    assign(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.doctorPatients.index],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.users.index],
        });
      },
    });
  };

  const handleUnassign = (targetUserId: number) => {
    if (!selectedUser) return;
    const payload = {
      doctor_id: filters.role === 'Doctor' ? selectedUser.id : targetUserId,
      user_id: filters.role === 'Customer' ? selectedUser.id : targetUserId,
    };
    unassign(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.doctorPatients.index],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.users.index],
        });
      },
    });
  };

  // Get available and assigned users for the dialog
  const getAvailableUsers = () => {
    if (!selectedUser) return [];
    return oppositeUsers.filter((user) => !assignedIds.includes(user.id));
  };

  const getAssignedUsers = () => {
    if (!selectedUser) return [];
    return oppositeUsers.filter((user) => assignedIds.includes(user.id));
  };

  return (
    <>
      <DashboardTableAccordion
        value="Assigning modifications"
        title="Assigning modifications"
        accordionOpenValue={accordionOpenValue}
        onOpen={handleAccordionOpen}
        data={users}
        isLoading={isLoading}
        emptyMessage="No users found."
        loadingMessage="Loading users..."
        paginationMeta={meta}
        onPageChange={setPage}
        filtersComponent={
          <AssigningModificationsFilters
            key={filtersResetKey}
            filters={filters}
            onFiltersChange={(newFilters) => {
              setFilters(newFilters);
              setPage(1);
            }}
          />
        }
        renderHeader={() => (
          <>
            <TableHead className="w-12"></TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            {filters.role !== 'Customer' && <TableHead>Clinic</TableHead>}
            <TableHead className="w-24"></TableHead>
          </>
        )}
        renderRow={(user, index) => (
          <>
            <TableCell>
              {(page - 1) * (meta?.per_page || 10) + index + 1}
            </TableCell>
            <TableCell>
              {user.name} {user.surname}
            </TableCell>
            <TableCell>{user.email}</TableCell>
            {filters.role !== 'Customer' && (
              <TableCell>{getClinicDisplay(user)}</TableCell>
            )}
            <TableCell className="text-right">
              <RelationActionButton onClick={() => handleModify(user)}>
                Modify
              </RelationActionButton>
            </TableCell>
          </>
        )}
      />

      <ModifyRelationsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedUser={selectedUser}
        userType={filters.role === 'Customer' ? 'Customer' : 'Doctor'}
        availableUsers={getAvailableUsers()}
        assignedUsers={getAssignedUsers()}
        onAssign={handleAssign}
        onUnassign={handleUnassign}
      />
    </>
  );
}
